import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageSquare, Plus, Send, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const ForumPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedPost, setExpandedPost] = useState(null);
  const [newPost, setNewPost] = useState({ title: "", content: "", category: "general" });
  const [replyContent, setReplyContent] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [posting, setPosting] = useState(false);

  const categories = [
    { value: "all", label: "All Topics" },
    { value: "general", label: "General Discussion" },
    { value: "strategies", label: "Sales Strategies" },
    { value: "marketing", label: "Marketing Tips" },
    { value: "success", label: "Success Stories" },
    { value: "support", label: "Support & Help" }
  ];

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      const url = selectedCategory === "all" 
        ? `${API}/forum/posts`
        : `${API}/forum/posts?category=${selectedCategory}`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error("Please fill in all fields");
      return;
    }

    setPosting(true);
    try {
      await axios.post(`${API}/forum/posts`, newPost);
      toast.success("Post created!");
      setDialogOpen(false);
      setNewPost({ title: "", content: "", category: "general" });
      fetchPosts();
    } catch (error) {
      toast.error("Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  const handleReply = async (postId) => {
    if (!replyContent.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    try {
      await axios.post(`${API}/forum/posts/${postId}/reply`, { content: replyContent });
      toast.success("Reply posted!");
      setReplyContent("");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to post reply");
    }
  };

  const togglePost = (postId) => {
    setExpandedPost(expandedPost === postId ? null : postId);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="forum-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Affiliate Community</p>
            <h1 className="text-4xl font-bold text-white">Forum</h1>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-gold flex items-center gap-2" data-testid="new-post-btn">
                <Plus className="w-4 h-4" /> New Post
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Post</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Input
                    placeholder="Post title"
                    value={newPost.title}
                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]"
                    data-testid="new-post-title"
                  />
                </div>
                <div>
                  <Select 
                    value={newPost.category} 
                    onValueChange={(value) => setNewPost({ ...newPost, category: value })}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]" data-testid="new-post-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
                      {categories.filter(c => c.value !== 'all').map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Textarea
                    placeholder="Share your thoughts, strategies, or questions..."
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)] min-h-[150px]"
                    data-testid="new-post-content"
                  />
                </div>
                <Button
                  onClick={handleCreatePost}
                  disabled={posting}
                  className="w-full btn-gold"
                  data-testid="submit-post-btn"
                >
                  {posting ? <div className="spinner w-4 h-4" /> : "Post"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="category-filter">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                selectedCategory === cat.value
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-[#121212] text-[#A3A3A3] hover:text-white border border-[rgba(255,255,255,0.05)]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-[#121212] h-32 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="empty-state" data-testid="empty-forum">
            <MessageSquare className="empty-state-icon" />
            <h3 className="text-xl text-white mb-2">No posts yet</h3>
            <p className="text-[#737373]">Be the first to start a discussion!</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="posts-list">
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="forum-post"
                data-testid={`forum-post-${post.id}`}
              >
                <div 
                  className="cursor-pointer"
                  onClick={() => togglePost(post.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="badge-gold text-xs">{post.category}</span>
                        <span className="text-xs text-[#737373]">
                          {new Date(post.created_at).toLocaleDateString('en-IN')}
                        </span>
                      </div>
                      <h3 className="text-lg font-medium text-white mb-2">{post.title}</h3>
                      <p className="text-[#A3A3A3] text-sm line-clamp-2">{post.content}</p>
                      <p className="text-xs text-[#737373] mt-2">
                        By {post.author_name} • {post.replies?.length || 0} replies
                      </p>
                    </div>
                    <button className="text-[#737373]">
                      {expandedPost === post.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedPost === post.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.05)]"
                  >
                    <p className="text-[#A3A3A3] mb-6">{post.content}</p>

                    {/* Replies */}
                    {post.replies?.length > 0 && (
                      <div className="space-y-3 mb-6">
                        {post.replies.map((reply) => (
                          <div key={reply.id} className="forum-reply">
                            <p className="text-white mb-2">{reply.content}</p>
                            <p className="text-xs text-[#737373]">
                              {reply.author_name} • {new Date(reply.created_at).toLocaleDateString('en-IN')}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply Input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Write a reply..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)] flex-1"
                        data-testid={`reply-input-${post.id}`}
                      />
                      <Button
                        onClick={() => handleReply(post.id)}
                        className="btn-gold px-4"
                        data-testid={`reply-btn-${post.id}`}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ForumPage;
